import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Col,
  CustomInput,
  FormGroup,
  Label,
  Row,
  TabContent,
  TabPane,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import { useAuth } from '../../../../commons/Auth';
import { useLoading } from '../../../../commons/Loading';
import Header from '../../../../commons/Header';
import { ReactComponent as IconDocument } from '../../../../images/icn_document.svg';
import { ReactComponent as IconBack } from '../../../../images/icn-back.svg';
import {
  BtnOutlineOrange,
  ContFilterYear,
} from '../../../../styled-components';
import variables from '../../../../variables';
import InvoiceIafsTable from './InvoiceIafsTable';
import EnergyIafsTable from './EnergyIafsTable';
import EnergyRecurringIafsTable from './EnergyRecurringIafsTable';
import InsuranceIafsTable from './InsuranceIafsTable';
import SignalIafsTable from './SignalIafsTable';
import generateYearOptions from '../../../../lib/helpers/generateYearOptions';
import SummaryTable from './SummaryTable';
import { formatDateTime } from '../../../../lib/helpers/date';

function InfoPresentational({ profile, year, onYearChange, anchorId, onBack }) {
  const [{ newToken } = {}] = useAuth();
  const [t] = useTranslation();
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();
  const [invoicesIafs, setInvoicesIafs] = useState([]);
  const [energyIafs, setEnergyIafs] = useState([]);
  const [energyRecurringIafs, setEnergyRecurringIafs] = useState([]);
  const [insurancesIafs, setInsurancesIafs] = useState([]);
  const [signalsIafs, setSignalsIafs] = useState([]);
  const [updatedAt, setUpdatedAt] = useState();

  const summaryRows = [
    {
      service: 'Compensi da fatture fornitore',
      amount: invoicesIafs.reduce((acc, elem) => acc + (elem.amount || 0), 0),
      totalServices: invoicesIafs.reduce(
        (acc, elem) => acc + (elem.totalServices || 0),
        0
      ),
    },
    {
      service: 'Compensi da fee energia',
      amount: energyIafs.reduce((acc, elem) => acc + (elem.amount || 0), 0),
      totalServices: energyIafs.reduce(
        (acc, elem) => acc + (elem.totalServices || 0),
        0
      ),
    },
    {
      service: 'Compensi da ricorrenze energia',
      amount: energyRecurringIafs.reduce(
        (acc, elem) => acc + (elem.amount || 0),
        0
      ),
      totalServices: energyRecurringIafs.reduce(
        (acc, elem) => acc + (elem.totalServices || 0),
        0
      ),
    },
    {
      service: 'Compensi da segnalazioni',
      amount: signalsIafs.reduce((acc, elem) => acc + (elem.amount || 0), 0),
      totalServices: signalsIafs.reduce(
        (acc, elem) => acc + (elem.totalServices || 0),
        0
      ),
    },
  ];

  useEffect(() => {
    if (!profile?._id || !newToken) return undefined;

    let isMounted = true;
    const fetchIafs = async () => {
      showLoading();

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/give-and-take/${profile._id}/iafs`,
          {
            headers: { Authorization: newToken },
            params: { year },
          }
        );

        const bonuses = Array.isArray(response.data) ? response.data : [];

        if (isMounted) {
          setUpdatedAt(response.headers['x-updated-at']);
          setInvoicesIafs(
            bonuses.filter(({ iafType }) => iafType === 'INVOICES')
          );
          setEnergyIafs(bonuses.filter(({ iafType }) => iafType === 'ENERGY'));
          setEnergyRecurringIafs(
            bonuses.filter(({ iafType }) => iafType === 'RECURRENT_ENERGY')
          );
          setInsurancesIafs(
            bonuses.filter(({ iafType }) => iafType === 'INSURANCES')
          );
          setSignalsIafs(
            bonuses.filter(({ iafType }) => iafType === 'SIGNALS')
          );
        }
      } catch (error) {
        if (isMounted) alert.error(t('COMMON:GENERIC_ERROR'));
      } finally {
        hideLoading();
      }
    };

    fetchIafs();
    return () => {
      isMounted = false;
    };
  }, [profile?._id, newToken, year]);

  useEffect(() => {
    if (!anchorId) return undefined;

    const timeoutId = window.setTimeout(() => {
      document.getElementById(anchorId)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [
    anchorId,
    invoicesIafs,
    energyIafs,
    energyRecurringIafs,
    insurancesIafs,
    signalsIafs,
  ]);

  return (
    <TabContent activeTab={'1'}>
      <TabPane tabId="1">
        <section>
          <Header
            title="Dare e avere - IAF ricevuti"
            icon={<IconDocument style={{ height: '25px', color: '#ffffff' }} />}
          />
          <Row className="m-t-20" style={{ marginBottom: '20px ' }}>
            <Col xs="12">
              <BtnOutlineOrange type="button" onClick={onBack}>
                <IconBack fill={variables.primary} /> Torna a Dare e avere
              </BtnOutlineOrange>
            </Col>
          </Row>

          <Row
            className="search-add-cont m-t-20"
            style={{ rowGap: '1rem', marginBottom: '1rem' }}
          >
            <Col sm="12" md="6" lg="3" className="select-wrapper">
              <ContFilterYear
                className="no-width"
                style={{ marginBottom: '0' }}
              >
                <Row>
                  <Col xs="6">
                    <Label>{t('GIVE_AND_TAKE:SEARCH_BY_YEAR')}</Label>
                  </Col>
                  <Col xs="6">
                    <FormGroup>
                      <CustomInput
                        type="select"
                        id="selectYear"
                        name="year"
                        value={year}
                        onChange={onYearChange}
                      >
                        {generateYearOptions()}
                      </CustomInput>
                    </FormGroup>
                  </Col>
                </Row>
              </ContFilterYear>
            </Col>
            {updatedAt && (
              <Col
                sm="12"
                md="6"
                lg="9"
                className="text-right"
                style={{ alignSelf: 'center', fontWeight: 'bold' }}
              >
                {t('GIVE_AND_TAKE:LAST_UPDATED')}: {formatDateTime(updatedAt)}
              </Col>
            )}
          </Row>
          <div
            style={{ display: 'flex', flexDirection: 'column', rowGap: '2rem' }}
          >
            <SummaryTable title="Riepilogo totali" rows={summaryRows} />
            <InvoiceIafsTable iafs={invoicesIafs} />
            <EnergyIafsTable iafs={energyIafs} />
            <EnergyRecurringIafsTable iafs={energyRecurringIafs} />
            <InsuranceIafsTable iafs={insurancesIafs} />
            <SignalIafsTable iafs={signalsIafs} />
          </div>
        </section>
      </TabPane>
    </TabContent>
  );
}

export default InfoPresentational;
